import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { 
  ArrowLeft,
  Edit, 
  Trash2, 
  Eye, 
  Search,
  Filter,
  MoreVertical,
  FolderOpen,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw
} from 'lucide-react';
import { Row, Col, Button, Form, Table, Card, Badge, InputGroup, Dropdown } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';
import ImprovedPagination from './common/ImprovedPagination';
import './Dashboard.css';
import './CompanyProjects.css';

const STORAGE_KEY_BASE = 'companyProjectsState';

function readStoredCompanyProjectsState(companyId) {
  if (!companyId) return null;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_KEY_BASE}:${companyId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function coerceNumber(val, fallback) {
  const n = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

const CompanyProjects = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { user } = useUser();

  const saved = readStoredCompanyProjectsState(companyId);

  const [company, setCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState(() =>
    typeof saved?.searchTerm === 'string' ? saved.searchTerm : ''
  );
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    typeof saved?.searchTerm === 'string' ? saved.searchTerm : ''
  );
  const [filterStatus, setFilterStatus] = useState(() => saved?.filterStatus ?? 'all');
  const [activeTab, setActiveTab] = useState(() => saved?.activeTab ?? 'projects');
  const [currentPage, setCurrentPage] = useState(() => coerceNumber(saved?.currentPage, 1));
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    if (saved && typeof saved.itemsPerPage === 'number') {
      return coerceNumber(saved.itemsPerPage, 25);
    }
    const local = localStorage.getItem('companyProjectsItemsPerPage');
    return local ? parseInt(local, 10) : 25;
  });
  const [paginationInfo, setPaginationInfo] = useState({
    totalCount: 0,
    totalPages: 0,
    page: 1,
    limit: 25
  });
  const [sortColumn, setSortColumn] = useState(() => {
    if (!saved) return null;
    if (typeof saved.sortColumn === 'string' || saved.sortColumn === null) return saved.sortColumn;
    return null;
  });
  const [sortDirection, setSortDirection] = useState(() => saved?.sortDirection ?? 'asc');
  const skipDebouncedPageReset = useRef(true);
  const isFirstCompanyEffect = useRef(true);

  // Fetch company data with retry logic for transient database errors
  const fetchCompanyData = async (retryCount = 0) => {
    const maxRetries = 2;
    const retryDelay = 1000; // 1 second delay between retries
    
    try {
      const response = await apiService.getCompanyById(companyId);

      if (response.success) {
        setCompany({
          id: response.data.id,
          identificationNumber: response.data.identificationNo || `COMP${response.data.id.toString().padStart(3, '0')}`,
          name: response.data.company || 'Unknown Company',
          industry: response.data.branch || 'Unknown Industry',
          status: response.data.active ? 'Active' : 'Inactive'
        });
      } else {
        setError(response.message || 'Failed to load company information');
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      const errorMessage = error.message || 'Failed to load company information';
      
      // Check if it's a database connection error, network error, or timeout and we haven't exceeded max retries
      const isRetryableError = errorMessage.toLowerCase().includes('database connection') || 
                             errorMessage.toLowerCase().includes('connection error') ||
                             errorMessage.toLowerCase().includes('database unavailable') ||
                             errorMessage.toLowerCase().includes('timeout') ||
                             errorMessage.toLowerCase().includes('network') ||
                             errorMessage.toLowerCase().includes('fetch') ||
                             error.name === 'TypeError' || // Network errors often throw TypeError
                             error.name === 'NetworkError';
      
      if (isRetryableError && retryCount < maxRetries) {
        console.log(`Retryable error detected, retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        // Retry the fetch
        return fetchCompanyData(retryCount + 1);
      }
      
      // If not a retryable error or max retries exceeded, show the error
      setError(errorMessage);
    }
  };

  // Fetch projects for the company with retry logic for transient database errors
  const fetchProjects = async (page = currentPage, limit = itemsPerPage, retryCount = 0) => {
    const maxRetries = 2;
    const retryDelay = 1000; // 1 second delay between retries
    
    try {
      setLoading(true);
      setError(null);
      // Determine type based on active tab: 'projects' tab = 'company', 'retailers' tab = 'retailer'
      const typeParam = activeTab === 'projects' ? 'company' : activeTab === 'retailers' ? 'retailer' : undefined;

      const response = await apiService.getProjectsByCompany(
        companyId,
        page,
        limit,
        typeParam,
        filterStatus,
        debouncedSearch
      );

      // The API returns: { success: true, data: [...], totalCount, totalPages, page, limit }
      if (response.success && Array.isArray(response.data)) {
        setProjects(response.data);
        // Update pagination info from backend response
        setPaginationInfo({
          totalCount: response.totalCount || 0,
          totalPages: response.totalPages || 0,
          page: response.page || page,
          limit: response.limit || limit
        });
      } else if (Array.isArray(response)) {
        setProjects(response);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      const errorMessage = error.message || 'Failed to fetch projects';
      
      // Check if it's a database connection error, network error, or timeout and we haven't exceeded max retries
      const isRetryableError = errorMessage.toLowerCase().includes('database connection') || 
                             errorMessage.toLowerCase().includes('connection error') ||
                             errorMessage.toLowerCase().includes('database unavailable') ||
                             errorMessage.toLowerCase().includes('timeout') ||
                             errorMessage.toLowerCase().includes('network') ||
                             errorMessage.toLowerCase().includes('fetch') ||
                             error.name === 'TypeError' || // Network errors often throw TypeError
                             error.name === 'NetworkError';
      
      if (isRetryableError && retryCount < maxRetries) {
        console.log(`Retryable error detected, retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        // Retry the fetch
        return fetchProjects(page, limit, retryCount + 1);
      }
      
      // If not a retryable error or max retries exceeded, show the error
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Scroll to top when component mounts or companyId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [companyId]);

  useEffect(() => {
    // Fetch company data
    // Retry logic is built into the fetch functions to handle transient database errors
    fetchCompanyData();
    
    // Force Poppins font on all elements in this component
    const style = document.createElement('style');
    style.textContent = `
      .company-projects-wrapper *,
      .company-projects-wrapper *::before,
      .company-projects-wrapper *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [companyId]);

  useEffect(() => {
    if (searchTerm === debouncedSearch) return;
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm, debouncedSearch]);

  // After debounced search updates, move to page 1 before the fetch effect runs (same tick as layout)
  useLayoutEffect(() => {
    if (skipDebouncedPageReset.current) {
      skipDebouncedPageReset.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Fetch projects when page, filters, or company changes (status/search applied on server)
  useEffect(() => {
    if (companyId) {
      fetchProjects(currentPage, itemsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, companyId, activeTab, filterStatus, debouncedSearch]);

  // When switching companies in-session (same mount), load that company's saved filters from sessionStorage
  useEffect(() => {
    if (isFirstCompanyEffect.current) {
      isFirstCompanyEffect.current = false;
      return;
    }
    skipDebouncedPageReset.current = true;
    const next = readStoredCompanyProjectsState(companyId);
    if (!next) {
      setSearchTerm('');
      setDebouncedSearch('');
      setFilterStatus('all');
      setActiveTab('projects');
      setCurrentPage(1);
      const local = localStorage.getItem('companyProjectsItemsPerPage');
      setItemsPerPage(local ? parseInt(local, 10) : 25);
      setSortColumn(null);
      setSortDirection('asc');
      return;
    }
    setSearchTerm(typeof next.searchTerm === 'string' ? next.searchTerm : '');
    setDebouncedSearch(typeof next.searchTerm === 'string' ? next.searchTerm : '');
    setFilterStatus(next.filterStatus ?? 'all');
    setActiveTab(next.activeTab ?? 'projects');
    setCurrentPage(coerceNumber(next.currentPage, 1));
    setItemsPerPage(coerceNumber(next.itemsPerPage, 25));
    if (typeof next.sortColumn === 'string' || next.sortColumn === null) {
      setSortColumn(next.sortColumn);
    }
    if (typeof next.sortDirection === 'string') {
      setSortDirection(next.sortDirection);
    }
  }, [companyId]);

  const handleDeleteProject = (projectId) => {
    setProjects(prevProjects => prevProjects.filter(project => project.id !== projectId));
  };

  const handleViewProjectDetails = (project) => {
    navigate(`/company/${companyId}/project/${project.id}`, {
      state: {
        project: project,
        company: company
      }
    });
  };

  const handleEditProject = (projectId) => {
    // This will be handled by the ProjectDetails component
    navigate(`/company/${companyId}/project/${projectId}`);
  };

  // Helper function to get status category
  const getStatusCategory = (status) => {
    const statusLower = status.toLowerCase();
    
    // Active statuses
    if (statusLower === 'sold' || statusLower === 'final' || statusLower === 'active') {
      return 'active';
    }
    
    // Pending statuses
    if (statusLower === 'budget' || statusLower === 'tracking' || 
        statusLower === 'estimating' || statusLower === 'ready to bid' || 
        statusLower === 'bid' || statusLower === 'promising') {
      return 'pending';
    }
    
    // Closed statuses
    if (statusLower === 'no bid' || statusLower === 'not built' || 
        statusLower === 'lost' || statusLower === 'closed') {
      return 'closed';
    }
    
    // Default for any other status
    return 'other';
  };

  // Helper function to get status sort order (Active = 1, Pending = 2, Closed = 3, Other = 4)
  const getStatusSortOrder = (status) => {
    const category = getStatusCategory(status);
    switch (category) {
      case 'active':
        return 1;
      case 'pending':
        return 2;
      case 'closed':
        return 3;
      default:
        return 4;
    }
  };

  // Helper function to parse Customer # for numeric sorting
  const parseCustomerNumber = (custProjNo) => {
    if (!custProjNo || custProjNo.trim() === '') {
      return { isEmpty: true, numericValue: 0 };
    }
    // Extract numeric part from the string (e.g., "T00022" -> 22)
    const numericMatch = custProjNo.match(/\d+/);
    if (numericMatch) {
      return { isEmpty: false, numericValue: parseInt(numericMatch[0], 10) };
    }
    // If no numeric part, use string comparison
    return { isEmpty: false, numericValue: 0, stringValue: custProjNo.toLowerCase() };
  };

  // Sort projects
  const sortedProjects = [...projects].sort((a, b) => {
    // If manual sort is selected, use that
    if (sortColumn) {
      let aValue, bValue;

      switch (sortColumn) {
        case 'custProjNo':
          aValue = (a.custProjNo || '').toLowerCase();
          bValue = (b.custProjNo || '').toLowerCase();
          break;
        case 'projectName':
          aValue = (a.projectName || '').toLowerCase();
          bValue = (b.projectName || '').toLowerCase();
          break;
        case 'tylerNumber':
          aValue = (a.tylerNumber || '').toLowerCase();
          bValue = (b.tylerNumber || '').toLowerCase();
          break;
        case 'tylerProjectName':
          aValue = (a.tylerProjectName || '').toLowerCase();
          bValue = (b.tylerProjectName || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    }

    // Default sort: Status (Active > Pending > Closed), then Customer # for Active items
    const aStatusOrder = getStatusSortOrder(a.status);
    const bStatusOrder = getStatusSortOrder(b.status);

    // First sort by status order
    if (aStatusOrder !== bStatusOrder) {
      return aStatusOrder - bStatusOrder;
    }

    // If both are Active, sort by Customer # (lowest to highest, empty first)
    if (aStatusOrder === 1 && bStatusOrder === 1) {
      const aCustNo = parseCustomerNumber(a.custProjNo);
      const bCustNo = parseCustomerNumber(b.custProjNo);

      // Empty values come first
      if (aCustNo.isEmpty && !bCustNo.isEmpty) return -1;
      if (!aCustNo.isEmpty && bCustNo.isEmpty) return 1;
      if (aCustNo.isEmpty && bCustNo.isEmpty) return 0;

      // Both have numeric values, sort numerically
      if (aCustNo.numericValue !== bCustNo.numericValue) {
        return aCustNo.numericValue - bCustNo.numericValue;
      }

      // If numeric values are equal or both are non-numeric, sort by string
      if (aCustNo.stringValue && bCustNo.stringValue) {
        return aCustNo.stringValue.localeCompare(bCustNo.stringValue);
      }
      return (a.custProjNo || '').toLowerCase().localeCompare((b.custProjNo || '').toLowerCase());
    }

    // For non-Active items with same status, maintain original order
    return 0;
  });

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get sort icon for a column
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={16} className="me-1 text-muted" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp size={16} className="me-1 text-primary" />
      : <ArrowDown size={16} className="me-1 text-primary" />;
  };

  // Persist view state for this company in session storage
  useEffect(() => {
    const storageKey = `${STORAGE_KEY_BASE}:${companyId}`;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          searchTerm,
          filterStatus,
          activeTab,
          itemsPerPage: coerceNumber(itemsPerPage, 25),
          currentPage: coerceNumber(currentPage, 1),
          sortColumn,
          sortDirection
        })
      );
    } catch (err) {
      console.error('Failed to persist company projects state', err);
    }
  }, [
    companyId,
    searchTerm,
    filterStatus,
    activeTab,
    itemsPerPage,
    currentPage,
    sortColumn,
    sortDirection
  ]);

  // Reset to page 1 when items per page changes and coerce to number
  const handleItemsPerPageChange = (newItemsPerPage) => {
    const parsed = Number(newItemsPerPage) || 0;
    setItemsPerPage(parsed > 0 ? parsed : 25);
    setCurrentPage(1);
  };

  // Use backend pagination info
  // Projects are already paginated from backend, just apply client-side filters/search/sort
  const totalPages = paginationInfo.totalPages || 1;
  const totalItems = paginationInfo.totalCount || 0;
  // Apply client-side filtering, searching, and sorting to the current page of results
  const currentProjects = sortedProjects;

  const getStatusBadge = (status) => {
    const statusLower = status.toLowerCase();
    
    // Active statuses
    if (statusLower === 'sold' || statusLower === 'final' || statusLower === 'active') {
      return <Badge bg="success">Active</Badge>;
    }
    
    // Pending statuses
    if (statusLower === 'budget' || statusLower === 'tracking' || 
        statusLower === 'estimating' || statusLower === 'ready to bid' || 
        statusLower === 'bid' || statusLower === 'promising') {
      return <Badge bg="warning" text="dark">Pending</Badge>;
    }
    
    // Closed statuses
    if (statusLower === 'no bid' || statusLower === 'not built' || 
        statusLower === 'lost' || statusLower === 'closed') {
      return <Badge bg="secondary">Closed</Badge>;
    }
    
    // Default for any other status
    return <Badge bg="light" text="dark">{status}</Badge>;
  };

  return (
    <div className="company-projects-wrapper">
      <div className="modern-dashboard">
        {/* Header */}
        <div className="dashboard-header-section mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                {user?.role === 'superAdmin' && (
                  <Button 
                    variant="link" 
                    className="p-0 me-3 text-decoration-none text-dark"
                    style={{ color: '#1f2937' }}
                    onClick={() => navigate('/companies')}
                  >
                    <ArrowLeft size={20} />
                  </Button>
                )}
                <div>
                  <h1 className="dashboard-title mb-1">{company?.name || 'Loading...'}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="nav-tabs-custom">
          <div className={`nav-item ${activeTab === 'projects' ? 'active' : ''}`}>
            <span 
              className={`nav-link ${activeTab === 'projects' ? 'active' : ''}`}
              onClick={() => setActiveTab('projects')}
              style={{ cursor: 'pointer' }}
            >
              Projects
            </span>
          </div>
          <div className="nav-item">
            <span 
              className="nav-link"
              onClick={() => navigate(`/company/${companyId}/retailers`)}
              style={{ cursor: 'pointer' }}
            >
              Retail
            </span>
          </div>
          <div className="nav-item">
            <span 
              className="nav-link"
              onClick={() => navigate(`/company/${companyId}/users`)}
              style={{ cursor: 'pointer' }}
            >
              Users
            </span>
          </div>
        </div>

        {/* Filters and Search */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={9}>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="closed">Closed</option>
                </Form.Select>
              </Col>
            </Row>
            
            {/* Pagination moved to search panel */}
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef', paddingTop: '1rem' }}>
              <ImprovedPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  // fetchProjects will be called by useEffect when currentPage changes
                }}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={(newItemsPerPage) => {
                  handleItemsPerPageChange(newItemsPerPage);
                  // fetchProjects will be called by useEffect when itemsPerPage changes
                }}
                showItemsPerPageSelector={true}
                showInfo={true}
                className="py-0"
              />
            </div>
          </Card.Body>
        </Card>


        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading projects...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div>
                <strong>Error loading projects:</strong> {error}
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  className="ms-3"
                  onClick={fetchProjects}
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Projects Table */}
        {!loading && !error && (
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Projects List</h5>
              <div className="d-flex align-items-center gap-3">
                <span className="text-muted">{sortedProjects.length} projects found</span>
                <Button 
                  variant="outline-light" 
                  size="sm"
                  onClick={fetchProjects}
                  disabled={loading}
                >
                  <RefreshCw size={16} className={`me-2 ${loading ? 'spin' : ''}`} />
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
                <Table responsive className="modern-table mb-0">
              <thead>
                <tr>
                  <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('custProjNo')}
                  >
                    <div className="d-flex align-items-center">
                      {getSortIcon('custProjNo')}
                      Project #
                    </div>
                  </th>
                  <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('projectName')}
                  >
                    <div className="d-flex align-items-center">
                      {getSortIcon('projectName')}
                      Project Name
                    </div>
                  </th>
                  <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('tylerNumber')}
                  >
                    <div className="d-flex align-items-center">
                      {getSortIcon('tylerNumber')}
                      Tyler #
                    </div>
                  </th>
                  <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('tylerProjectName')}
                  >
                    <div className="d-flex align-items-center">
                      {getSortIcon('tylerProjectName')}
                      Tyler Project Name
                    </div>
                  </th>
                  <th 
                  style={{ cursor: 'pointer', textAlign: 'right' }}
                  onClick={() => handleSort('status')}
                  >
                    <div className="d-flex align-items-center justify-content-end">
                      {getSortIcon('status')}
                      Status
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5">
                      <FolderOpen size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">No projects found</h5>
                      <p className="text-muted">This company doesn't have any projects yet.</p>
                    </td>
                  </tr>
                ) : (
                  currentProjects.map((project) => {
                    const displayCustProjNo = project.custProjNo || project.tylerNumber || '';
                    const displayProjectName = project.projectName || project.tylerProjectName || '';
                    const useFallbackCust = !project.custProjNo && !!project.tylerNumber;
                    const useFallbackProject = !project.projectName && !!project.tylerProjectName;
                    return (
                    <tr 
                      key={project.id} 
                      className="project-row"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleViewProjectDetails(project)}
                    >
                      <td>
                        <div className="d-flex align-items-center">
                          <ChevronRight size={16} className="me-2 text-muted" />
                          <span style={useFallbackCust ? { color: '#D3D3D3' } : undefined}>
                            {displayCustProjNo}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span style={useFallbackProject ? { color: '#D3D3D3' } : undefined}>
                          {displayProjectName}
                        </span>
                      </td>
                      <td>{project.tylerNumber || ''}</td>
                      <td>{project.tylerProjectName || ''}</td>
                      <td style={{ textAlign: 'right' }}>{getStatusBadge(project.status)}</td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
        )}
      </div>
    </div>
  );
};

export default CompanyProjects;

